import React from 'react';
import { UseFormSetValue, UseFormWatch } from 'react-hook-form';

import Button from '@/components/shared/Button/Button';
import { TextField } from '@/components/shared/TextField/TextField';

import { BlockFormData } from '../schema';

type PersonalizationsFieldProps = {
  watch: UseFormWatch<BlockFormData>;
  setValue: UseFormSetValue<BlockFormData>;
};

export const PersonalizationsField = ({ watch, setValue }: PersonalizationsFieldProps) => {
  const watchedPersonalizations = watch('personalizations');
  const [newPersonalization, setNewPersonalization] = React.useState('');
  const [dragIndex, setDragIndex] = React.useState<number | null>(null);

  const addPersonalization = () => {
    if (newPersonalization.trim() && !watchedPersonalizations.includes(newPersonalization.trim())) {
      setValue('personalizations', [...watchedPersonalizations, newPersonalization.trim()], {
        shouldDirty: true,
      });
      setNewPersonalization('');
    }
  };

  const removePersonalization = (tag: string) => {
    setValue(
      'personalizations',
      watchedPersonalizations.filter((t) => t !== tag),
      { shouldDirty: true },
    );
  };

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (event: React.DragEvent<HTMLSpanElement>) => {
    event.preventDefault();
  };

  const handleDrop = (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) return;
    const updated = [...watchedPersonalizations];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(targetIndex, 0, moved);
    setValue('personalizations', updated, { shouldDirty: true });
    setDragIndex(null);
  };

  return (
    <div className="mb-4">
      <label className="block text-sm font-semibold text-neutral-70 mb-2">Tags</label>
      <div className="flex flex-wrap gap-2 mb-3">
        {watchedPersonalizations.map((tag, index) => (
          <span
            key={tag}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(index)}
            className="flex gap-2 border border-neutral-20 rounded-lg px-2 py-1 cursor-move"
          >
            {tag}
            <button
              type="button"
              onClick={() => removePersonalization(tag)}
              className="text-warning-60 font-bold"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-6">
        <TextField
          value={newPersonalization}
          onChange={(e) => setNewPersonalization(e.target.value)}
          className="!w-full font-medium"
          inputClassName="font-medium !border !border-neutral-20 !font-medium"
          placeholder="Example tag"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addPersonalization();
            }
          }}
        />
        <Button size="s" onClick={addPersonalization} label="+ Add Tag" />
      </div>
    </div>
  );
};

export default PersonalizationsField;
